// Copyright 2026 Bitech.id. Licensed under the MIT License.

const DEFAULT_MAX_SIZE = 50;
const DEFAULT_FLUSH_INTERVAL_MS = 5000;
const DEFAULT_MAX_MEMORY_EVENTS = 1000;
const MAX_FLUSH_RETRIES = 3;
const RETRY_DELAY_MS = 500;

export interface EventBufferOptions<T> {
  maxSize?: number;
  flushInterval?: number;
  maxMemoryEvents?: number;
  /** Name used in the drop warning, e.g. "log" or "pulse". */
  label: string;
  /** Transport hook — throws/rejects on failure so EventBuffer can retry, per its own cap. */
  onFlush: (batch: T[]) => Promise<void>;
}

/**
 * Buffers events of a given shape and flushes them as a batch — distinct
 * from captureException's immediate-send behavior. Delivery is best-effort: a
 * hard crash (SIGKILL) or exceeding maxMemoryEvents before a flush completes
 * can still lose buffered entries (oldest evicted first).
 */
export class EventBuffer<T> {
  private queue: T[] = [];
  private readonly timer: ReturnType<typeof setInterval>;
  private readonly maxSize: number;
  private readonly maxMemoryEvents: number;
  private readonly label: string;
  private readonly onFlush: (batch: T[]) => Promise<void>;
  private flushing = false;

  constructor(options: EventBufferOptions<T>) {
    this.maxSize = options.maxSize ?? DEFAULT_MAX_SIZE;
    this.maxMemoryEvents = options.maxMemoryEvents ?? DEFAULT_MAX_MEMORY_EVENTS;
    this.label = options.label;
    this.onFlush = options.onFlush;

    this.timer = setInterval(
      () => void this.flush(),
      options.flushInterval ?? DEFAULT_FLUSH_INTERVAL_MS,
    );
    // Don't let this timer alone keep a Node process alive.
    (this.timer as unknown as { unref?: () => void }).unref?.();
  }

  add(event: T): void {
    this.queue.push(event);
    if (this.queue.length > this.maxMemoryEvents) {
      this.queue.shift();
    }
    if (this.queue.length >= this.maxSize) {
      void this.flush();
    }
  }

  /** Adds the event and flushes immediately — for urgent (e.g. warn/error) events. */
  addAndFlush(event: T): void {
    this.add(event);
    void this.flush();
  }

  async flush(): Promise<void> {
    if (this.flushing || this.queue.length === 0) return;

    this.flushing = true;
    const batch = this.queue.splice(0, this.queue.length);
    try {
      await this.attemptFlush(batch, 0);
    } finally {
      this.flushing = false;
    }
  }

  /** Stops the flush timer and performs one last best-effort flush. */
  destroy(): void {
    clearInterval(this.timer);
    void this.flush();
  }

  private async attemptFlush(batch: T[], retryCount: number): Promise<void> {
    try {
      await this.onFlush(batch);
    } catch (err) {
      if (retryCount >= MAX_FLUSH_RETRIES) {
        console.warn(`[Biwave] failed to send ${this.label} batch after retries, dropping it`, err);
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
      await this.attemptFlush(batch, retryCount + 1);
    }
  }
}
